var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html } from 'lit';
import { state } from 'lit/decorators.js';
export class KRRouter {
    /**
     * The current route's resolved data: everything every matched level's static resolve() returned,
     * merged with deeper levels winning on a shared key.
     *
     * Read it in connectedCallback — the router fills it before any element mounts. A child route gets
     * what its parent resolved from here, so a tab component needs no resolve() of its own. Use
     * KRRouter.getParams() for the route's params.
     */
    static getData() {
        return KRRouter.data;
    }
    static configure(config) {
        KRRouter.base = config.base;
        KRRouter.routes = config.routes;
        // Point the page's outlet class at this router. The Operations shell loads krubble globally while
        // an embedded app bundles its own copy, so the kr-router-outlet that won registration may belong
        // to the other copy; whichever it is, its outlets must register with the router that has routes.
        const outletClass = customElements.get('kr-router-outlet');
        if (outletClass) {
            outletClass.router = KRRouter;
        }
        // Intercept <a> clicks for client-side navigation
        window.addEventListener('click', (e) => {
            const anchor = e.composedPath().find(el => el instanceof HTMLAnchorElement);
            if (!anchor) {
                return;
            }
            if (anchor.origin !== window.location.origin) {
                return;
            }
            if (!anchor.pathname.startsWith(KRRouter.base)) {
                return;
            }
            if (anchor.target === '_blank') {
                return;
            }
            // Cmd/Ctrl-click (new tab), Shift-click (new window) and Alt-click (download) are browser
            // affordances — leave them to the browser rather than hijacking them into an in-app navigation.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                return;
            }
            e.preventDefault();
            KRRouter.navigate(anchor.pathname);
        });
        // Process each navigation. popstate covers every source — a link click, a KRRouter.navigate()
        // call (which dispatches one itself), and the browser's back/forward buttons.
        window.addEventListener('popstate', () => {
            KRRouter.handleNavigation();
        });
        // The initial page load fires no popstate, so process the URL the app booted on. Deferred to a
        // microtask rather than run synchronously: configure() is called while app.js is being evaluated
        // as an import, which happens BEFORE the importing module's body. Navigating synchronously here
        // would fire the first resolve() fetches before main.ts had run KRHttp.configure(), so they'd go
        // to the dev server's own origin (localhost:4201/api/...) instead of devUrl and 404.
        Promise.resolve().then(() => {
            KRRouter.handleNavigation();
        });
    }
    static handleNavigation() {
        const matched = KRRouter.matchRoutes();
        // Nothing matched (a stale or mistyped link). An unrecognized URL
        // shouldn't destroy the state of the screen the user is on.
        if (!matched.routes.length) {
            return;
        }
        // A route with children but no child segment in the URL resolves to its `default` child, so the
        // resolved url is longer than what's in the address bar (/pages/42 resolves pages/42/details).
        // Rewrite it so the URL reflects what's actually on screen.
        let resolvedPath = KRRouter.base;
        if (matched.url) {
            resolvedPath = KRRouter.base + '/' + matched.url;
        }
        if (window.location.pathname !== resolvedPath) {
            window.history.replaceState({}, '', resolvedPath);
        }
        // Drop cached entries for routes DEEPER than the URL we're navigating to — if the user navigates
        // up a level we assume they're moving on to a different task. This is what keeps the cache from
        // growing without bound.
        for (const key of KRRouter.routeCache.keys()) {
            if (matched.url.split('/').filter(s => s).length < key.split('/').filter(s => s).length) {
                KRRouter.routeCache.delete(key);
            }
        }
        const token = ++KRRouter.navToken;
        // Resolve every route's matching component (screen), keeping each level's resolved data alongside
        // its element so the whole chain can be merged once they've all settled.
        Promise.all(matched.routes.map(match => {
            // Reuse the cached element for this url if we have one. Its data is cached with it — resolve()
            // doesn't run again unless the route asks for it with runResolvers: 'always' — but the level still
            // has to contribute to the merge below.
            const cached = KRRouter.routeCache.get(match.url);
            if (cached) {
                const cachedConstructor = customElements.get(match.route.component);
                if (match.route.runResolvers === 'always' && cachedConstructor && typeof cachedConstructor.resolve === 'function') {
                    return Promise.resolve(cachedConstructor.resolve(KRRouter.getParams())).then(data => {
                        cached.data = data;
                        return {
                            el: cached.el,
                            data,
                        };
                    });
                }
                return Promise.resolve({
                    el: cached.el,
                    data: cached.data,
                });
            }
            // Create and cache the screen element
            const el = document.createElement(match.route.component);
            const entry = {
                el,
                scrolls: [],
                data: null,
            };
            KRRouter.routeCache.set(match.url, entry);
            // If the component class defines a static resolve(params), await it. Every level's result is
            // merged into KRRouter.data below before anything mounts, so a component paints with its data
            // already in hand instead of flashing empty.
            const constructor = customElements.get(match.route.component);
            if (constructor && typeof constructor.resolve === 'function') {
                return Promise.resolve(constructor.resolve(KRRouter.getParams())).then(data => {
                    entry.data = data;
                    return {
                        el,
                        data,
                    };
                });
            }
            return Promise.resolve({
                el,
                data: null,
            });
        }))
            .then(resolved => {
            // A newer navigation started while we were resolving — its elements are the current truth.
            if (token !== KRRouter.navToken) {
                return;
            }
            // Merge the whole chain's resolved data into one object, parents first so a deeper route can
            // override a key it shares with an ancestor. Set BEFORE any element mounts, so every component
            // — at any depth — reads it with KRRouter.getData() in connectedCallback: a tab component gets
            // what its parent resolved with no resolve() of its own and no second request. Mirrors
            // KNNavigator.data() in kodaris-ngx, which searches the Angular route tree by key.
            KRRouter.data = {};
            for (const item of resolved) {
                if (item.data) {
                    Object.assign(KRRouter.data, item.data);
                }
            }
            const elements = resolved.map(item => item.el);
            // ---- PASS 1: deactivate, deepest level first ----
            // Remove screen elements that no longer apply to the new current routes for the current url
            const levels = Array.from(KRRouter.outletContexts.keys()).sort((a, b) => b - a);
            for (const level of levels) {
                const context = KRRouter.outletContexts.get(level);
                if (!context?.outlet?.el || context.outlet.el === elements[level]) {
                    continue;
                }
                // Capture the outgoing element's scroll positions into its cache entry, then unmount it.
                const outgoing = KRRouter.routeCache.get(context.url);
                if (outgoing) {
                    outgoing.scrolls = KRRouter.getScrollableElements(context.outlet.el).map(scrollable => scrollable.scrollTop);
                }
                context.outlet.deactivate();
                context.element = null;
                context.url = '';
            }
            // Put each screen element into the appropriate router outlet
            elements.forEach((el, level) => {
                // First navigation to reach this depth has no context yet.
                let context = KRRouter.outletContexts.get(level);
                if (!context) {
                    context = {
                        outlet: null,
                        element: null,
                        url: '',
                    };
                    KRRouter.outletContexts.set(level, context);
                }
                context.element = el;
                context.url = matched.routes[level].url;
                if (context.outlet && context.outlet.el !== el) {
                    context.outlet.activate(el);
                    // Put back the scroll positions captured when the user last left this element.
                    const cached = KRRouter.routeCache.get(context.url);
                    if (cached?.scrolls.length) {
                        setTimeout(() => {
                            const scrollables = KRRouter.getScrollableElements(cached.el);
                            cached.scrolls.forEach((top, index) => {
                                if (scrollables[index]) {
                                    scrollables[index].scrollTop = top;
                                }
                            });
                        }, 0);
                    }
                }
            });
        });
    }
    // An outlet announces itself here when it connects and takes whatever element its context holds.
    static registerOutlet(level, outlet) {
        // No context yet when an outlet connects before a navigation
        let context = KRRouter.outletContexts.get(level);
        if (!context) {
            context = {
                outlet: null,
                element: null,
                url: '',
            };
            KRRouter.outletContexts.set(level, context);
        }
        context.outlet = outlet;
        // An outlet re-attached with its element already mounted (a cached parent coming back) must not
        // restore scroll again — that would overwrite the live position with a stale one.
        if (context.element && outlet.el !== context.element) {
            outlet.activate(context.element);
            // Same scroll restore as the activation pass in handleNavigation — this is the path taken when
            // the outlet didn't exist yet at navigation time, which for us is the common case.
            const cached = KRRouter.routeCache.get(context.url);
            if (cached?.scrolls.length) {
                setTimeout(() => {
                    const scrollables = KRRouter.getScrollableElements(cached.el);
                    cached.scrolls.forEach((top, index) => {
                        if (scrollables[index]) {
                            scrollables[index].scrollTop = top;
                        }
                    });
                }, 0);
            }
        }
    }
    static unregisterOutlet(level, outlet) {
        const context = KRRouter.outletContexts.get(level);
        if (context && context.outlet === outlet) {
            context.outlet = null;
        }
    }
    static navigate(path) {
        let fullPath = path;
        if (!path.startsWith('/')) {
            fullPath = KRRouter.base + '/' + path;
        }
        window.history.pushState({}, '', fullPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
    }
    /**
     * Rewrite the current url in place, keeping the mounted screen as it is — no popstate is dispatched,
     * so nothing in flight (a streaming response, an open editor) is torn down. For when a screen's url
     * becomes more specific after a server round-trip: a new chat's first message resolves a conversation
     * id, a new record's save yields its key.
     *
     * Use this instead of a bare history.replaceState. The cache files each screen under its url, so a raw
     * rewrite would leave the screen keyed by the OLD url: navigating back to that url would be a cache hit
     * handing back this same screen with all its state — a chat rewritten from `new` to `all/123` would
     * reopen conversation 123 on the next New Chat click instead of starting fresh. So the deepest level's
     * cache entry is re-filed under the new url, and its outlet context follows.
     */
    static replace(path) {
        const before = KRRouter.matchRoutes();
        let fullPath = path;
        if (!path.startsWith('/')) {
            fullPath = KRRouter.base + '/' + path;
        }
        window.history.replaceState({}, '', fullPath);
        const after = KRRouter.matchRoutes();
        if (!before.routes.length || !after.routes.length) {
            return;
        }
        // The caller is the deepest matched screen rewriting its own url; shallower levels keep theirs.
        const level = before.routes.length - 1;
        const oldUrl = before.routes[level].url;
        const newUrl = after.routes[after.routes.length - 1].url;
        if (oldUrl === newUrl) {
            return;
        }
        const entry = KRRouter.routeCache.get(oldUrl);
        if (entry) {
            KRRouter.routeCache.delete(oldUrl);
            KRRouter.routeCache.set(newUrl, entry);
        }
        const context = KRRouter.outletContexts.get(level);
        if (context && context.url === oldUrl) {
            context.url = newUrl;
        }
    }
    static getParams() {
        const matched = KRRouter.matchRoutes();
        const params = {};
        for (const match of matched.routes) {
            for (const key in match.params) {
                params[key] = decodeURIComponent(match.params[key]);
            }
        }
        return params;
    }
    // Every scrollable element in and under `root`, in document order — piercing shadow roots.
    static getScrollableElements(root) {
        const scrollables = [];
        // Depth-first pre-order, each node's shadow root before its light children. An explicit stack
        // rather than recursion; pushing children in reverse makes them pop in document order, which keeps
        // the sequence identical to the recursive walk this replaces. The ORDER is load-bearing: capture
        // and restore index into the returned list positionally, so both passes must enumerate the same
        // nodes in the same sequence.
        const stack = [root];
        while (stack.length) {
            const node = stack.pop();
            if (!node) {
                break;
            }
            const style = getComputedStyle(node);
            if (style.overflow === 'auto'
                || style.overflow === 'scroll'
                || style.overflowY === 'auto'
                || style.overflowY === 'scroll') {
                scrollables.push(node);
            }
            // Shadow children first so they occupy the same positions the recursion gave them.
            const children = [];
            if (node.shadowRoot) {
                children.push(...Array.from(node.shadowRoot.children));
            }
            children.push(...Array.from(node.children));
            for (let index = children.length - 1; index >= 0; index--) {
                stack.push(children[index]);
            }
        }
        return scrollables;
    }
    // Match the CURRENT browser URL against the route defs.
    //
    // Example, with base '/operations/app/ai-cms' and the routes configured in app.ts:
    //
    //   URL      /operations/app/ai-cms/pages/42/seo
    //   segments ['pages', '42', 'seo']        (base stripped, split on '/')
    //   returns  {
    //              routes: [
    //                { route: { path: 'pages/:contentPageID', component: 'kt-page' },     params: { contentPageID: '42' }, url: 'pages/42' },
    //                { route: { path: 'seo',                  component: 'kt-page-seo' }, params: {},                     url: 'pages/42/seo' },
    //              ],
    //              url: 'pages/42/seo',
    //            }
    //
    // Three other shapes worth knowing:
    //
    //   .../pages/42   a route with children but no child segment resolves its `default` child, so this
    //                  returns BOTH levels and a url of 'pages/42/details' — longer than the address
    //                  bar. handleNavigation() is what syncs the URL to it; this function is pure and
    //                  never touches history.
    //   .../          (no segments) matches the path:'' route, returning a single entry for kt-home.
    //   .../nope      no route matches, returning no routes and an empty url.
    static matchRoutes(routes = KRRouter.routes, segments) {
        // An empty array is a legitimate "nothing left to match" from a recursive call and stays as-is —
        // only a genuinely absent argument (the entry call) reaches for the browser URL.
        if (!segments) {
            let relative = window.location.pathname;
            if (relative.startsWith(KRRouter.base)) {
                relative = relative.slice(KRRouter.base.length);
            }
            segments = relative.split('/').filter(s => s);
        }
        for (const route of routes) {
            const routeSegments = route.path.split('/').filter(s => s);
            // index route {path: '', component: kt-home}
            if (routeSegments.length === 0 && segments.length === 0) {
                return {
                    routes: [{
                            route,
                            params: {},
                            url: ''
                        }],
                    url: ''
                };
            }
            if (routeSegments.length === 0) {
                continue;
            }
            if (segments.length < routeSegments.length) {
                continue;
            }
            const params = {};
            let matched = true;
            for (let i = 0; i < routeSegments.length; i++) {
                if (routeSegments[i].startsWith(':')) {
                    params[routeSegments[i].slice(1)] = segments[i];
                }
                else if (routeSegments[i] !== segments[i]) {
                    matched = false;
                    break;
                }
            }
            if (!matched) {
                continue;
            }
            const remaining = segments.slice(routeSegments.length);
            // This level's own resolved URL: the segments it consumed, with :params swapped for their real
            // values. Children come back from the recursion holding only THEIR portion, so we prefix each
            // with this as the recursion unwinds — every entry ends up with the full path from the root.
            const url = segments.slice(0, routeSegments.length).join('/');
            const matchedRoutes = [{
                    route,
                    params,
                    url
                }];
            if (remaining.length > 0) {
                // Segments left over and nowhere to put them — this route doesn't fully match the URL, so try
                // the next one. Without this, a route would be accepted having consumed only part of the URL:
                // /pages/42/seo would match {path: 'pages'} and render the Pages list. It also narrows
                // route.children for the call below, which matters because matchRoutes' first parameter has a
                // DEFAULT — passing undefined would silently re-match from the top-level routes rather than
                // failing.
                if (!route.children) {
                    continue;
                }
                const childMatch = KRRouter.matchRoutes(route.children, remaining);
                if (childMatch.routes.length > 0) {
                    for (const child of childMatch.routes) {
                        child.url = url + '/' + child.url;
                    }
                    matchedRoutes.push(...childMatch.routes);
                    return {
                        routes: matchedRoutes,
                        url: url + '/' + childMatch.url
                    };
                }
            }
            // No remaining segments + a default child — resolve as though that child's segment were in the
            // URL. We do NOT rewrite the address bar here; matching stays pure. handleNavigation() syncs the
            // URL to the resolved `url` this returns.
            if (route.children && remaining.length === 0 && route.default) {
                const childMatch = KRRouter.matchRoutes(route.children, [route.default]);
                if (childMatch.routes.length > 0) {
                    for (const child of childMatch.routes) {
                        child.url = url + '/' + child.url;
                    }
                    matchedRoutes.push(...childMatch.routes);
                    return {
                        routes: matchedRoutes,
                        url: url + '/' + childMatch.url
                    };
                }
            }
            return {
                routes: matchedRoutes,
                url
            };
        }
        return {
            routes: [],
            url: ''
        };
    }
}
KRRouter.base = '';
KRRouter.routes = [];
KRRouter.routeCache = new Map();
// One context per nesting level — Angular's ChildrenOutletContexts, keyed by level rather than by
// outlet name since we only have one (primary) outlet per level. Outlets are DRIVEN through these;
// they never ask the router for anything.
KRRouter.outletContexts = new Map();
// One token per navigation. A slower navigation that has been superseded must not activate its
// (stale) elements, so it checks the token before committing anything.
KRRouter.navToken = 0;
// Every matched route level's resolved data, merged into one object for the current navigation. This
// is set before anything mounts — see the merge in handleNavigation.
KRRouter.data = {};
export class KRRouterOutlet extends LitElement {
    constructor() {
        super(...arguments);
        this.level = -1;
        // The element the router has told this outlet to show.
        this.el = null;
    }
    connectedCallback() {
        super.connectedCallback();
        // This outlet's nesting depth = how many kr-router-outlet ancestors it has, which is only
        // knowable once attached. Walk up through shadow boundaries too (via ShadowRoot.host), since a
        // nested outlet lives inside its parent element's shadow root and parentNode stops at the root.
        let level = 0;
        let node = this;
        while (node) {
            if (node instanceof ShadowRoot) {
                node = node.host;
                continue;
            }
            if (node instanceof HTMLElement && node.tagName === 'KR-ROUTER-OUTLET' && node !== this) {
                level++;
            }
            if (node instanceof HTMLElement) {
                node = node.parentNode;
            }
            else {
                node = null;
            }
        }
        this.level = level;
        KRRouterOutlet.router.registerOutlet(this.level, this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        KRRouterOutlet.router.unregisterOutlet(this.level, this);
    }
    activate(el) {
        this.el = el;
    }
    deactivate() {
        this.el = null;
    }
    render() {
        if (!this.el) {
            return html ``;
        }
        return html `${this.el}`;
    }
}
KRRouterOutlet.styles = css `
    :host {
      display: block;
      height: 100%;
    }
  `;
// The router outlets register with. KRRouter.configure() sets this on whichever outlet class is
// registered on the page, which may be another copy of krubble's; this copy's router is the default.
KRRouterOutlet.router = KRRouter;
__decorate([
    state()
], KRRouterOutlet.prototype, "el", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins; KRRouter.configure() then points that class at
// the configured router.
if (!customElements.get('kr-router-outlet')) {
    customElements.define('kr-router-outlet', KRRouterOutlet);
}
//# sourceMappingURL=router.js.map